"use client";

import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb, deleteSiteCascade, type OfflineSite } from "@/core/offline/db";
import { deleteWithOfflineFallback } from "@/core/offline/delete-with-fallback";
import { useOfflineSession } from "./useOfflineSession";

export function useSites() {
  const { session } = useOfflineSession();
  const sites = useLiveQuery(
    async () => {
      const all = await offlineDb.sites.toArray();
      return all.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
    }
  ) ?? [];

  async function createSite(data: {
    name: string;
    client?: string;
    address?: string;
  }): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date();

    await offlineDb.sites.add({
      id,
      name: data.name,
      client: data.client,
      address: data.address,
      userId: (session?.user as { id?: string })?.id ?? "local",
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending",
    });

    return id;
  }

  async function updateSite(
    id: string,
    data: Partial<Pick<OfflineSite, "name" | "client" | "address">>
  ) {
    await offlineDb.sites.update(id, {
      ...data,
      updatedAt: new Date(),
      syncStatus: "pending",
    });
  }

  /** Supprime un site côté serveur puis localement (cascade).
   *  Offline : supprime localement, la sync propagera au serveur. */
  async function deleteSite(id: string) {
    await deleteWithOfflineFallback("site", id, `/api/sites/${id}`, () =>
      deleteSiteCascade(id)
    );
  }

  return { sites, createSite, updateSite, deleteSite };
}
